// Ported from design/project/App10w-SomeoneWaiting.dc.html and App10x-WaitingGone.dc.html, then wired: a waiting
// alert's button (t.me/bot?startapp=w_<arena>_<side>_<stake>_<length>) opens this. While someone still waits on those
// terms, one tap takes the other side and the match starts at once; if they found someone, it offers a search.
import { useEffect, useState } from "react";
import { usdt } from "@degenduel/shared";
import { api, ApiError } from "../api.js";
import { payoutFor, useGame, useTicker } from "../game.js";
import { go } from "../router.js";
import { play } from "../sound.js";
import { arenaLabel, otherSide, WAITING_ALERT, type WaitingAlert } from "../challenge.js";

const ERRORS: Record<string, string> = {
  already_in_battle: "Finish your battle first.",
  already_queued: "You are already searching.",
  challenge_open: "You have a challenge open. Cancel it first.",
  arena_closed: "This arena is closed right now.",
  account_frozen: "Your account is paused while we check something. Your balance is safe and withdrawals still work.",
  on_break: "You're on a break from battling for now. Your balance is safe and withdrawals still work.",
  daily_limit: "That would go over your daily stake limit. It resets at 00:00 WAT.",
  maintenance: "Quick upgrade in progress. Try again in a few minutes.",
  arena_locked: "This arena isn't open yet.",
  stake_not_allowed: "This stake is above your limit for now.",
  network: "No connection. Check your data and try again.",
};

function readAlert(): WaitingAlert | null {
  try {
    return JSON.parse(sessionStorage.getItem(WAITING_ALERT) ?? "null") as WaitingAlert | null;
  } catch {
    return null;
  }
}

export function App10w_SomeoneWaiting() {
  const { me, now, findOpponent, topUp, feeNow, select } = useGame();
  useTicker(1000);
  const [alert] = useState(readAlert);
  const [since, setSince] = useState<number | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!alert) return void go("App09", true);
    let stop = false;
    const check = () =>
      api<{ waiting: boolean; since: number | null }>(
        `/v1/waiting?arena=${alert.arena}&side=${alert.side}&stake=${alert.stake}&durationSec=${alert.durationSec}`,
      )
        .then((r) => !stop && setSince(r.since))
        .catch(() => !stop && setSince((s) => (s === undefined ? null : s)));
    void check();
    const t = setInterval(check, 4_000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, []);

  if (!alert || since === undefined) return <div className="screen" />;
  const take = otherSide(alert.side);
  const gone = since === null;
  const stake = alert.stake;
  const canAfford = !!me && usdt(me.balance.available) >= usdt(stake);
  const { payout } = payoutFor(stake, feeNow());
  const waited = since ? Math.max(0, now() - since) : 0;
  const waitedText = `${Math.floor(waited / 60_000)}:${String(Math.floor((waited % 60_000) / 1000)).padStart(2, "0")}`;
  const minutes = alert.durationSec === 60 ? "1 minute" : "3 minutes";

  const fight = async () => {
    if (busy || !me) return;
    if (!canAfford) return void topUp();
    play("tap");
    setBusy(true);
    setError(null);
    try {
      // The search answers theirs: same arena, stake and length, the other side. Matched at once while they wait.
      const terms = { arena: alert.arena, stake, custom: !["2", "10", "50", "250"].includes(stake), durationSec: alert.durationSec === 60 ? 60 : 180 } as const;
      // The search screen shows the lobby's selection, so it becomes these terms.
      select(terms);
      await findOpponent(take, terms, "App10w");
      // Kept until now: a failed join comes back to this screen, which reads it again.
      sessionStorage.removeItem(WAITING_ALERT);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : "network";
      if (code === "join_channel") return; // the channel screen has opened
      setError(code === "low_balance" ? `You need ${stake} USDT for this.` : ERRORS[code] ?? "Something went wrong. Try again.");
      setBusy(false);
    }
  };
  const leave = () => {
    sessionStorage.removeItem(WAITING_ALERT);
    go("App09", true);
  };
  const sideTag = (s: "long" | "short") => (s === "long" ? "▲ LONG" : "▼ SHORT");
  const label = !me ? "CONNECTING..." : busy ? "LOCKING STAKE..." : !canAfford ? "TOP UP TO PLAY" : gone ? `SEARCH ${take.toUpperCase()} · ${stake} USDT` : `TAKE ${take.toUpperCase()} AND FIGHT`;

  return (
    <>
      <div className="screen">
  <div className="htL" aria-hidden="true" style={{ position: "absolute", left: "0", top: "0", width: "100%", height: "470px", clipPath: "polygon(0 0, 100% 0, 100% 78%, 0 100%)" }}></div>
  <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-safe-bottom) + 8px)", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
    <div className="row tgbar" style={{ position: "absolute", left: "0", right: "0", justifyContent: "center" }}><span className="tag tagW">FROM A BOT ALERT</span></div>

    {gone ? (
      <>
        <div style={{ marginTop: "44px", alignSelf: "center", width: "96px", height: "96px", borderRadius: "26px", background: alert.side === "long" ? "#CCFF00" : "#FF3366", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Unbounded, sans-serif", fontWeight: "900", fontSize: "30px", color: "#0B0E14", opacity: "0.5" }}>?</div>
        <h1 className="h1" style={{ marginTop: "22px", textAlign: "center" }}>They found someone</h1>
        <p className="muted" style={{ margin: "8px 6px 0", textAlign: "center", fontSize: "14px", lineHeight: "21px" }}>Another player took the {take.toUpperCase()} side first. Search now and you'll meet the next {alert.side.toUpperCase()} on {arenaLabel(alert.arena)}.</p>
      </>
    ) : (
      <>
        <div style={{ marginTop: "24px", alignSelf: "center", textAlign: "center", transform: "skewX(-8deg)" }}>
          <div className="big" style={{ fontSize: "30px", color: "#FFFFFF" }}>SOMEONE'S</div>
          <div className="big" style={{ fontSize: "34px", color: "#CCFF00", textShadow: "4px 4px 0 #0B0E14, 8px 8px 0 #FF3366" }}>WAITING</div>
        </div>
        <div style={{ marginTop: "28px", alignSelf: "center", position: "relative" }}>
          <div className="avA" style={{ width: "120px", height: "120px", borderRadius: "28px", borderWidth: "4px", background: alert.side === "long" ? "#CCFF00" : "#FF3366", fontSize: "40px", boxShadow: "8px 8px 0 #0B0E14", animation: "ddPop 1.4s infinite" }}>?</div>
          <span className={alert.side === "long" ? "tag tagL" : "tag tagR"} style={{ position: "absolute", left: "50%", bottom: "-14px", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>{alert.side === "long" ? "LONG ▲" : "SHORT ▼"}</span>
        </div>
        <span className="unb" style={{ marginTop: "26px", alignSelf: "center", fontWeight: "900", fontSize: "18px" }}>A PLAYER</span>
        <span className="muted" style={{ marginTop: "4px", alignSelf: "center", fontSize: "13px", textAlign: "center" }}>thinks {arenaLabel(alert.arena)} {alert.side === "long" ? "pumps" : "drops"} in {minutes} · waiting {waitedText}</span>
      </>
    )}

    <div className="panel" style={{ marginTop: "22px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", padding: "14px 0" }}>
      <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>YOU TAKE</div><div className={take === "long" ? "mono lime" : "mono red"} style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{sideTag(take)}</div></div>
      <div style={{ textAlign: "center", borderLeft: "2px solid #30363D", borderRight: "2px solid #30363D" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>STAKE</div><div className="mono" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{stake} USDT</div></div>
      {gone
        ? <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>LENGTH</div><div className="mono" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{alert.durationSec === 60 ? "1:00" : "3:00"}</div></div>
        : <div style={{ textAlign: "center" }}><div className="unb muted" style={{ fontSize: "9px", fontWeight: "800" }}>WIN</div><div className="mono lime" style={{ fontWeight: "800", fontSize: "15px", marginTop: "4px" }}>{payout}</div></div>}
    </div>

    <div style={{ marginTop: "auto", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "14px" }}>
      {error && <span role="alert" className="red" style={{ textAlign: "center", fontSize: "13px" }}>{error}</span>}
      <button className="btnA" disabled={busy || !me} onClick={() => void fight()}>{label}</button>
      <button className="btnAS" onClick={leave}>{gone ? "BACK TO LOBBY" : "NOT NOW"}</button>
    </div>
  </div>
</div>
    </>
  );
}
